import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { Organization } from '../../models/organization';
import { OrganizationsService } from '../../services/organizations/organizations';
import { OrganizationUser } from '../../models/organization-user';
import { PageMeta } from '../../models/page-meta';
import { OrganizationUsersService } from '../../services/organization-users/organization-users';
import { OrganizationLicence } from '../../models/organization-licence';
import { LicencesService } from '../../services/licences/licences';
import {
  CreateApiCredentialResponse,
  OrganizationApiCredential
} from '../../models/organization-api-credential';
import { ApiCredentialsService } from '../../services/api-credentials/api-credentials';
import { LicenceReminderSetting } from '../../models/licence-reminder-setting';
import { LicenceRemindersService } from '../../services/licence-reminders/licence-reminders';

@Component({
  selector: 'app-organization-details',
  standalone: false,
  templateUrl: './organization-details.html',
  styleUrls: ['./organization-details.css']
})
export class OrganizationDetails implements OnInit {
  organizationId = '';
  organization: Organization | null = null;

  loading = false;
  activeTab: 'overview' | 'users' | 'licences' | 'credentials' | 'reminders' = 'overview';

  users: OrganizationUser[] = [];
  usersLoading = false;
  usersSearch = '';
  usersPage = 1;
  usersSize = 10;

  usersMeta: PageMeta = {
    page: 1,
    size: 10,
    totalItems: 0,
    totalPages: 0
  };

  createUserModel = {
    fullName: '',
    email: '',
    role: 3,
    receivesLicenceEmails: true
  };

  creatingUser = false;
  selectedUser: OrganizationUser | null = null;

  editUserModel = {
    fullName: '',
    email: '',
    role: 3,
    receivesLicenceEmails: true
  };

  updatingUser = false;

  licences: OrganizationLicence[] = [];
  licencesLoading = false;
  licencesPage = 1;
  licencesSize = 10;

  licencesMeta: PageMeta = {
    page: 1,
    size: 10,
    totalItems: 0,
    totalPages: 0
  };

  createLicenceModel = {
    durationDays: 30,
    gracePeriodDays: 7
  };

  creatingLicence = false;

  selectedLicence: OrganizationLicence | null = null;

  renewModel = {
    additionalDays: 30
  };

  renewingLicence = false;

  credentials: OrganizationApiCredential[] = [];
  credentialsLoading = false;
  credentialsPage = 1;
  credentialsSize = 10;

  credentialsMeta: PageMeta = {
    page: 1,
    size: 10,
    totalItems: 0,
    totalPages: 0
  };

  creatingCredential = false;
  newCredential: CreateApiCredentialResponse | null = null;

  reminderSetting: LicenceReminderSetting | null = null;
  reminderLoading = false;
  savingReminder = false;

  reminderModel = {
    isEnabled: true,
    reminderDaysBeforeExpiry: '30,14,7,3,1'
  };

  constructor(
    private route: ActivatedRoute,
    private organizationsService: OrganizationsService,
    private organizationUsersService: OrganizationUsersService,
    private licencesService: LicencesService,
    private apiCredentialsService: ApiCredentialsService,
    private licenceRemindersService: LicenceRemindersService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.organizationId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.organizationId) {
      this.toastr.error('Organization id was not found.');
      return;
    }

    this.loadOrganization();
  }

  loadOrganization(): void {
    this.loading = true;

    this.organizationsService.getOrganizationById(this.organizationId)
      .subscribe({
        next: response => {
          this.loading = false;

          if (!response.success || !response.data) {
            this.toastr.error(response.message || 'Unable to load organization.');
            this.cdr.detectChanges();
            return;
          }

          this.organization = response.data;
          this.cdr.detectChanges();
        },
        error: error => {
          this.loading = false;

          this.toastr.error(
            error?.error?.message ||
            error?.error?.error?.details ||
            'Unable to load organization.',
            'Organization'
          );
          this.cdr.detectChanges();
        }
      });
  }

  setTab(tab: 'overview' | 'users' | 'licences' | 'credentials' | 'reminders'): void {
    this.activeTab = tab;
    this.cdr.detectChanges();

    if (tab === 'users' && this.users.length === 0) {
      this.loadUsers();
    }

    if (tab === 'licences' && this.licences.length === 0) {
      this.loadLicences();
    }

    if (tab === 'credentials' && this.credentials.length === 0) {
      this.loadCredentials();
    }

    if (tab === 'reminders' && !this.reminderSetting) {
      this.loadReminderSetting();
    }
  }

  loadReminderSetting(): void {
    if (!this.organizationId) return;

    this.reminderLoading = true;

    this.licenceRemindersService.getSetting(this.organizationId)
      .subscribe({
        next: response => {
          this.reminderLoading = false;

          if (!response.success || !response.data) {
            this.toastr.error(response.message || 'Unable to load reminder settings.');
            this.cdr.detectChanges();
            return;
          }

          this.reminderSetting = response.data;

          this.reminderModel = {
            isEnabled: response.data.isEnabled,
            reminderDaysBeforeExpiry: response.data.reminderDaysBeforeExpiry
          };
          this.cdr.detectChanges();
        },
        error: error => {
          this.reminderLoading = false;

          this.toastr.error(
            error?.error?.message ||
            error?.error?.error?.details ||
            'Unable to load reminder settings.',
            'Reminder Settings'
          );
          this.cdr.detectChanges();
        }
      });
  }

  saveReminderSetting(): void {
    if (!this.reminderModel.reminderDaysBeforeExpiry.trim()) {
      this.toastr.warning('Reminder days are required.', 'Validation');
      return;
    }

    this.savingReminder = true;

    this.licenceRemindersService.updateSetting(this.organizationId, {
      isEnabled: this.reminderModel.isEnabled,
      reminderDaysBeforeExpiry: this.reminderModel.reminderDaysBeforeExpiry.trim()
    }).subscribe({
      next: response => {
        this.savingReminder = false;

        if (!response.success) {
          this.toastr.error(response.message || 'Unable to save reminder settings.');
          return;
        }

        if (response.data) {
          this.reminderSetting = response.data;
          this.reminderModel = {
            isEnabled: response.data.isEnabled,
            reminderDaysBeforeExpiry: response.data.reminderDaysBeforeExpiry
          };
        }

        this.toastr.success('Reminder settings saved successfully.');
        this.cdr.detectChanges();
      },
      error: error => {
        this.savingReminder = false;

        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to save reminder settings.',
          'Reminder Settings'
        );
        this.cdr.detectChanges();
      }
    });
  }

  loadCredentials(): void {
    if (!this.organizationId) return;

    this.credentialsLoading = true;

    this.apiCredentialsService
      .getCredentials(this.organizationId, this.credentialsPage, this.credentialsSize)
      .pipe(finalize(() => {
        this.credentialsLoading = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: response => {
          if (!response.success || !Array.isArray(response.data)) {
            this.credentials = [];
            this.credentialsMeta = this.createCredentialsMeta(0);
            this.toastr.error(response.message || 'Unable to load API credentials.');
            this.cdr.detectChanges();
            return;
          }

          this.credentials = response.data;
          this.credentialsMeta = response.meta || this.createCredentialsMeta(this.credentials.length);
          this.cdr.detectChanges();
        },
        error: error => {
          this.credentials = [];
          this.credentialsMeta = this.createCredentialsMeta(0);
          this.toastr.error(
            error?.error?.message ||
            error?.error?.error?.details ||
            'Unable to load API credentials.',
            'API Credentials'
          );
          this.cdr.detectChanges();
        }
      });
  }

  previousCredentialsPage(): void {
    if (this.credentialsPage <= 1) return;

    this.credentialsPage--;
    this.loadCredentials();
  }

  private createCredentialsMeta(totalItems: number): PageMeta {
    return {
      page: this.credentialsPage,
      size: this.credentialsSize,
      totalItems,
      totalPages: Math.max(1, Math.ceil(totalItems / this.credentialsSize))
    };
  }

  nextCredentialsPage(): void {
    if (this.credentialsPage >= this.credentialsMeta.totalPages) return;

    this.credentialsPage++;
    this.loadCredentials();
  }

  createCredential(): void {
    this.creatingCredential = true;
    this.newCredential = null;

    this.apiCredentialsService.createCredential(this.organizationId)
      .subscribe({
        next: response => {
          this.creatingCredential = false;

          if (!response.success || !response.data) {
            this.toastr.error(response.message || 'Unable to create API credential.');
            return;
          }

          this.newCredential = response.data;
          this.toastr.success('API credential created successfully.');

          this.loadCredentials();
        },
        error: error => {
          this.creatingCredential = false;

          this.toastr.error(
            error?.error?.message ||
            error?.error?.error?.details ||
            'Unable to create API credential.',
            'API Credentials'
          );
        }
      });
  }

  revokeCredential(credential: OrganizationApiCredential): void {
    const confirmed = confirm(`Are you sure you want to revoke ${credential.clientId}?`);

    if (!confirmed) return;

    this.apiCredentialsService.revokeCredential(this.organizationId, credential.id)
      .subscribe({
        next: response => {
          if (!response.success) {
            this.toastr.error(response.message || 'Unable to revoke credential.');
            return;
          }

          this.toastr.success('API credential revoked successfully.');
          this.loadCredentials();
        },
        error: error => {
          this.toastr.error(
            error?.error?.message ||
            error?.error?.error?.details ||
            'Unable to revoke credential.',
            'API Credentials'
          );
        }
      });
  }

  copyText(value: string): void {
    navigator.clipboard.writeText(value);
    this.toastr.success('Copied to clipboard.');
  }

  getCredentialStatusLabel(status: number): string {
    switch (status) {
      case 1:
        return 'Active';
      case 2:
        return 'Revoked';
      case 3:
        return 'Disabled';
      default:
        return 'Unknown';
    }
  }

  getCredentialStatusClass(status: number): string {
    switch (status) {
      case 1:
        return 'text-bg-success';
      case 2:
        return 'text-bg-danger';
      case 3:
        return 'text-bg-secondary';
      default:
        return 'text-bg-light';
    }
  }

  loadLicences(): void {
    if (!this.organizationId) return;

    this.licencesLoading = true;

    this.licencesService
      .getLicences(this.organizationId, this.licencesPage, this.licencesSize)
      .pipe(finalize(() => {
        this.licencesLoading = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: response => {
          if (!response.success || !Array.isArray(response.data)) {
            this.licences = [];
            this.licencesMeta = this.createLicencesMeta(0);
            this.toastr.error(response.message || 'Unable to load licences.');
            this.cdr.detectChanges();
            return;
          }

          this.licences = response.data;
          this.licencesMeta = response.meta || this.createLicencesMeta(this.licences.length);
          this.cdr.detectChanges();
        },
        error: error => {
          this.licences = [];
          this.licencesMeta = this.createLicencesMeta(0);
          this.toastr.error(
            error?.error?.message ||
            error?.error?.error?.details ||
            'Unable to load licences.',
            'Licences'
          );
          this.cdr.detectChanges();
        }
      });
  }

  previousLicencesPage(): void {
    if (this.licencesPage <= 1) return;

    this.licencesPage--;
    this.loadLicences();
  }

  private createLicencesMeta(totalItems: number): PageMeta {
    return {
      page: this.licencesPage,
      size: this.licencesSize,
      totalItems,
      totalPages: Math.max(1, Math.ceil(totalItems / this.licencesSize))
    };
  }

  nextLicencesPage(): void {
    if (this.licencesPage >= this.licencesMeta.totalPages) return;

    this.licencesPage++;
    this.loadLicences();
  }

  createLicence(): void {
    if (this.createLicenceModel.durationDays <= 0) {
      this.toastr.warning('Duration days must be greater than zero.', 'Validation');
      return;
    }

    if (this.createLicenceModel.gracePeriodDays < 0) {
      this.toastr.warning('Grace period days cannot be negative.', 'Validation');
      return;
    }

    this.creatingLicence = true;

    this.licencesService.createLicence(this.organizationId, {
      durationDays: Number(this.createLicenceModel.durationDays),
      gracePeriodDays: Number(this.createLicenceModel.gracePeriodDays)
    }).subscribe({
      next: response => {
        this.creatingLicence = false;

        if (!response.success) {
          this.toastr.error(response.message || 'Unable to create licence.');
          return;
        }

        this.toastr.success('Licence created successfully.');

        this.createLicenceModel = {
          durationDays: 30,
          gracePeriodDays: 7
        };

        const modalEl = document.getElementById('createLicenceModal');
        const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
        modal?.hide();

        this.loadLicences();
      },
      error: error => {
        this.creatingLicence = false;

        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to create licence.',
          'Licences'
        );
      }
    });
  }

  openRenewModal(licence: OrganizationLicence): void {
    this.selectedLicence = licence;
    this.renewModel = {
      additionalDays: 30
    };

    const modalEl = document.getElementById('renewLicenceModal');
    const modal = new (window as any).bootstrap.Modal(modalEl);
    modal.show();
  }

  renewLicence(): void {
    if (!this.selectedLicence) return;

    if (this.renewModel.additionalDays <= 0) {
      this.toastr.warning('Additional days must be greater than zero.', 'Validation');
      return;
    }

    this.renewingLicence = true;

    this.licencesService.renewLicence(
      this.organizationId,
      this.selectedLicence.id,
      { additionalDays: Number(this.renewModel.additionalDays) }
    ).subscribe({
      next: response => {
        this.renewingLicence = false;

        if (!response.success) {
          this.toastr.error(response.message || 'Unable to renew licence.');
          return;
        }

        this.toastr.success('Licence renewed successfully.');

        const modalEl = document.getElementById('renewLicenceModal');
        const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
        modal?.hide();

        this.loadLicences();
      },
      error: error => {
        this.renewingLicence = false;

        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to renew licence.',
          'Licences'
        );
      }
    });
  }

  changeLicenceStatus(licence: OrganizationLicence, action: 'suspend' | 'revoke'): void {
    const confirmed = confirm(`Are you sure you want to ${action} this licence?`);

    if (!confirmed) return;

    const request =
      action === 'suspend'
        ? this.licencesService.suspendLicence(this.organizationId, licence.id)
        : this.licencesService.revokeLicence(this.organizationId, licence.id);

    request.subscribe({
      next: response => {
        if (!response.success) {
          this.toastr.error(response.message || 'Unable to update licence.');
          return;
        }

        this.toastr.success(`Licence ${action}d successfully.`);
        this.loadLicences();
      },
      error: error => {
        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to update licence.',
          'Licences'
        );
      }
    });
  }

  getLicenceStatusLabel(status: number): string {
    switch (status) {
      case 1:
        return 'Draft';
      case 2:
        return 'Active';
      case 3:
        return 'Expired';
      case 4:
        return 'Suspended';
      case 5:
        return 'Revoked';
      default:
        return 'Unknown';
    }
  }

  getLicenceStatusClass(status: number): string {
    switch (status) {
      case 2:
        return 'text-bg-success';
      case 3:
        return 'text-bg-danger';
      case 4:
        return 'text-bg-warning';
      case 5:
        return 'text-bg-secondary';
      default:
        return 'text-bg-light';
    }
  }

  loadUsers(): void {
    if (!this.organizationId) return;

    this.usersLoading = true;

    this.organizationUsersService
      .getUsers(this.organizationId, this.usersPage, this.usersSize, this.usersSearch)
      .pipe(finalize(() => {
        this.usersLoading = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: response => {
          if (!response.success || !Array.isArray(response.data)) {
            this.users = [];
            this.usersMeta = this.createUsersMeta(0);
            this.toastr.error(response.message || 'Unable to load organization users.');
            this.cdr.detectChanges();
            return;
          }

          this.users = response.data;
          this.usersMeta = response.meta || this.createUsersMeta(this.users.length);
          this.cdr.detectChanges();
        },
        error: error => {
          this.users = [];
          this.usersMeta = this.createUsersMeta(0);
          this.toastr.error(
            error?.error?.message ||
            error?.error?.error?.details ||
            'Unable to load organization users.',
            'Organization Users'
          );
          this.cdr.detectChanges();
        }
      });
  }

  searchUsers(): void {
    this.usersPage = 1;
    this.loadUsers();
  }

  clearUsersSearch(): void {
    this.usersSearch = '';
    this.usersPage = 1;
    this.loadUsers();
  }

  previousUsersPage(): void {
    if (this.usersPage <= 1) return;

    this.usersPage--;
    this.loadUsers();
  }

  private createUsersMeta(totalItems: number): PageMeta {
    return {
      page: this.usersPage,
      size: this.usersSize,
      totalItems,
      totalPages: Math.max(1, Math.ceil(totalItems / this.usersSize))
    };
  }

  nextUsersPage(): void {
    if (this.usersPage >= this.usersMeta.totalPages) return;

    this.usersPage++;
    this.loadUsers();
  }

  createOrganizationUser(): void {
    if (!this.createUserModel.fullName.trim()) {
      this.toastr.warning('Full name is required.', 'Validation');
      return;
    }

    if (!this.createUserModel.email.trim()) {
      this.toastr.warning('Email is required.', 'Validation');
      return;
    }

    this.creatingUser = true;

    this.organizationUsersService.createUser(this.organizationId, {
      fullName: this.createUserModel.fullName.trim(),
      email: this.createUserModel.email.trim(),
      role: this.createUserModel.role,
      receivesLicenceEmails: this.createUserModel.receivesLicenceEmails
    }).subscribe({
      next: response => {
        this.creatingUser = false;

        if (!response.success) {
          this.toastr.error(response.message || 'Unable to create organization user.');
          return;
        }

        this.toastr.success('Organization user created successfully.');

        this.createUserModel = {
          fullName: '',
          email: '',
          role: 3,
          receivesLicenceEmails: true
        };

        const modalEl = document.getElementById('createOrganizationUserModal');
        const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
        modal?.hide();

        this.loadUsers();
      },
      error: error => {
        this.creatingUser = false;

        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to create organization user.',
          'Organization Users'
        );
      }
    });
  }

  openEditUserModal(user: OrganizationUser): void {
    this.selectedUser = user;
    this.editUserModel = {
      fullName: user.fullName,
      email: user.email,
      role: user.role,
      receivesLicenceEmails: user.receivesLicenceEmails
    };

    const modalEl = document.getElementById('editOrganizationUserModal');
    const modal = new (window as any).bootstrap.Modal(modalEl);
    modal.show();
  }

  updateOrganizationUser(): void {
    if (!this.selectedUser) return;

    if (!this.editUserModel.fullName.trim()) {
      this.toastr.warning('Full name is required.', 'Validation');
      return;
    }

    if (!this.editUserModel.email.trim()) {
      this.toastr.warning('Email is required.', 'Validation');
      return;
    }

    this.updatingUser = true;

    this.organizationUsersService.updateUser(this.organizationId, this.selectedUser.id, {
      fullName: this.editUserModel.fullName.trim(),
      email: this.editUserModel.email.trim(),
      role: this.editUserModel.role,
      receivesLicenceEmails: this.editUserModel.receivesLicenceEmails
    }).subscribe({
      next: response => {
        this.updatingUser = false;

        if (!response.success) {
          this.toastr.error(response.message || 'Unable to update organization user.');
          return;
        }

        this.toastr.success('Organization user updated successfully.');

        const modalEl = document.getElementById('editOrganizationUserModal');
        const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
        modal?.hide();

        this.loadUsers();
      },
      error: error => {
        this.updatingUser = false;

        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to update organization user.',
          'Organization Users'
        );
      }
    });
  }

  changeUserStatus(user: OrganizationUser, action: 'activate' | 'disable'): void {
    const confirmed = confirm(`Are you sure you want to ${action} ${user.email}?`);

    if (!confirmed) return;

    const request =
      action === 'activate'
        ? this.organizationUsersService.activateUser(this.organizationId, user.id)
        : this.organizationUsersService.disableUser(this.organizationId, user.id);

    request.subscribe({
      next: response => {
        if (!response.success) {
          this.toastr.error(response.message || 'Unable to update user status.');
          return;
        }

        this.toastr.success(`Organization user ${action}d successfully.`);
        this.loadUsers();
      },
      error: error => {
        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to update user status.',
          'Organization Users'
        );
      }
    });
  }

  getUserRoleLabel(role: number): string {
    switch (role) {
      case 1:
        return 'Owner';
      case 2:
        return 'Admin';
      case 3:
        return 'User';
      default:
        return 'Unknown';
    }
  }

  getUserStatusLabel(status: number): string {
    return status === 1 ? 'Active' : 'Disabled';
  }

  getUserStatusClass(status: number): string {
    return status === 1 ? 'text-bg-success' : 'text-bg-secondary';
  }

  getStatusLabel(status: number): string {
    switch (status) {
      case 1:
        return 'Active';
      case 2:
        return 'Suspended';
      case 3:
        return 'Disabled';
      default:
        return 'Unknown';
    }
  }

  getStatusClass(status: number): string {
    switch (status) {
      case 1:
        return 'text-bg-success';
      case 2:
        return 'text-bg-warning';
      case 3:
        return 'text-bg-secondary';
      default:
        return 'text-bg-light';
    }
  }
}
